/**
 * FX Rates page — manual-only rate management.
 *
 * Users add and view FX rates manually (e.g. when creating LCs).
 * No live API fetching — all rates are entered by hand.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatDate } from "@bizop/shared/dates";
import {
  Badge,
  Button,
  Card,
  DataTable,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  mockFxRatesFinance,
  type MockFxRate,
} from "../../../lib/financeMock";
import {
  FxRateForm,
  type FxRateFormValues,
} from "../../../components/forms/FxRateForm";

export const Route = createFileRoute("/_app/finance/fx")({
  component: FxRatesPage,
});

function FxRatesPage() {
  const [rows, setRows] = usePersistentMockState<MockFxRate[]>(
    "fxRates",
    mockFxRatesFinance,
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<MockFxRate | null>(null);

  // Group by pair for the "current rates" panel.
  const currentByPair = useMemo(() => {
    const map = new Map<string, MockFxRate>();
    for (const r of rows) {
      const key = `${r.fromCurrency}→${r.toCurrency}`;
      const existing = map.get(key);
      if (!existing || r.effectiveAt > existing.effectiveAt) {
        map.set(key, r);
      }
    }
    return Array.from(map.values());
  }, [rows]);

  const columns = useMemo<ColumnDef<MockFxRate>[]>(
    () => [
      {
        id: "pair",
        header: "Pair",
        cell: ({ row }) => (
          <span className="font-mono text-[12px] text-(--text-primary)">
            {row.original.fromCurrency} &rarr; {row.original.toCurrency}
          </span>
        ),
        meta: { mono: true },
      },
      {
        id: "rate",
        header: "Rate",
        meta: { numeric: true, mono: true },
        cell: ({ row }) => (
          <span className="font-mono tabular-nums text-(--text-primary)">
            {(Number(row.original.rateE8) / 1e8).toFixed(4)}
          </span>
        ),
      },
      {
        accessorKey: "effectiveAt",
        header: "Effective",
        cell: ({ row }) => (
          <span className="text-(--text-secondary) tabular-nums text-[12px]">
            {formatDate(row.original.effectiveAt, { style: "medium" })}
          </span>
        ),
      },
      {
        accessorKey: "source",
        header: "Source",
        cell: ({ row }) => (
          <Badge variant="neutral" size="sm">
            {row.original.source.replace(/_/g, " ")}
          </Badge>
        ),
      },
      {
        accessorKey: "notes",
        header: "Notes",
        cell: ({ row }) => (
          <span className="text-(--text-tertiary) text-[12px]">
            {row.original.notes ?? "—"}
          </span>
        ),
      },
    ],
    [],
  );

  function handleCreate(values: FxRateFormValues & { rateE8: bigint }) {
    const newRow: MockFxRate = {
      _id: `fx_local_${Date.now()}`,
      fromCurrency: values.fromCurrency,
      toCurrency: values.toCurrency,
      rateE8: values.rateE8,
      effectiveAt: new Date(values.effectiveAt).getTime(),
      source: values.source,
      notes: values.notes || null,
    };
    setRows((p) => [newRow, ...p]);
    setCreateOpen(false);
    toast.success(`${values.fromCurrency}→${values.toCurrency} rate added`);
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="FX Rates"
        description="Manually set reference rates for foreign-currency LCs, loans, and expenses. Historical rates are preserved for accurate replay."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> New rate
          </Button>
        }
      />

      {/* Current rates */}
      <Card variant="default" className="p-0">
        <div className="flex items-center justify-between border-b border-(--border-subtle) px-4 py-3">
          <h2 className="text-[14px] font-semibold tracking-tight text-(--text-primary)">
            Current rates
          </h2>
          <span className="text-[11px] text-(--text-tertiary)">Latest entry per pair</span>
        </div>
        {currentByPair.length === 0 ? (
          <div className="px-4 py-8 text-center text-[13px] text-(--text-tertiary)">
            No rates yet. Click <span className="font-medium">New rate</span> to add one.
          </div>
        ) : (
          <ul className="divide-y divide-(--border-subtle)">
            {currentByPair.map((p) => (
              <li
                key={p._id}
                className="flex items-center justify-between gap-3 px-4 py-3 text-[13px]"
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono text-(--text-primary)">
                    {p.fromCurrency} &rarr; {p.toCurrency}
                  </span>
                  <Badge variant="neutral" size="sm">
                    {p.source.replace(/_/g, " ")}
                  </Badge>
                </div>
                <div className="text-right">
                  <div className="font-mono tabular-nums text-(--text-primary) font-semibold">
                    {(Number(p.rateE8) / 1e8).toFixed(4)}
                  </div>
                  <div className="text-[11px] text-(--text-tertiary) tabular-nums">
                    {formatDate(p.effectiveAt, { style: "short" })}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Rate history table */}
      <Card variant="default" className="p-0">
        <div className="flex items-center justify-between border-b border-(--border-subtle) px-4 py-3">
          <h2 className="text-[14px] font-semibold tracking-tight text-(--text-primary)">
            Rate history
          </h2>
          <span className="text-[11px] text-(--text-tertiary)">All entries</span>
        </div>
        <DataTable
          data={[...rows].sort((a, b) => b.effectiveAt - a.effectiveAt)}
          columns={columns}
          getRowId={(r) => r._id}
          onRowClick={(row) => setDetailRow(row.original)}
        />
      </Card>

      {/* Create Sheet */}
      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>New FX rate</SheetTitle>
            <SheetDescription>
              Each entry is a new historical row — old rates remain so historical reports stay accurate.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <FxRateForm
              onSubmit={handleCreate}
              onCancel={() => setCreateOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Detail Sheet */}
      <Sheet
        open={detailRow !== null}
        onOpenChange={(open) => {
          if (!open) setDetailRow(null);
        }}
      >
        <SheetContent side="right" size="md">
          {detailRow ? (
            <FxRateDetail
              rate={detailRow}
              onClose={() => setDetailRow(null)}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function FxRateDetail({
  rate,
  onClose,
}: {
  rate: MockFxRate;
  onClose: () => void;
}) {
  const rateDecimal = (Number(rate.rateE8) / 1e8).toFixed(6);
  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3">
          <span className="font-mono">
            {rate.fromCurrency} &rarr; {rate.toCurrency}
          </span>
          <Badge variant="neutral" size="sm">
            {rate.source.replace(/_/g, " ")}
          </Badge>
        </SheetTitle>
        <SheetDescription>
          Effective {formatDate(rate.effectiveAt, { style: "long" })}
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <div className="flex flex-col gap-5">
          <section className="grid grid-cols-2 gap-3">
            <FxField label="Pair" value={`${rate.fromCurrency} / ${rate.toCurrency}`} />
            <FxField label="Rate" value={rateDecimal} />
            <FxField
              label="Effective"
              value={formatDate(rate.effectiveAt, { style: "medium" })}
            />
            <FxField label="Source" value={rate.source.replace(/_/g, " ")} />
          </section>

          {rate.notes ? (
            <section>
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-1">
                Notes
              </div>
              <p className="text-[13px] text-(--text-secondary) whitespace-pre-line">
                {rate.notes}
              </p>
            </section>
          ) : null}

          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
              Conversion examples
            </div>
            <table className="w-full text-[13px]">
              <tbody>
                {[1, 100, 1_000, 10_000].map((amt) => (
                  <tr
                    key={amt}
                    className="border-b border-(--border-subtle) last:border-0"
                  >
                    <td className="py-2 font-mono tabular-nums">
                      {amt.toLocaleString()} {rate.fromCurrency}
                    </td>
                    <td className="py-2 text-right font-mono tabular-nums text-(--text-primary)">
                      {(amt * (Number(rate.rateE8) / 1e8)).toLocaleString(
                        "en-BD",
                        { maximumFractionDigits: 2 },
                      )}{" "}
                      {rate.toCurrency}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      </SheetBody>
      <SheetFooter>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </SheetFooter>
    </>
  );
}

function FxField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary) font-mono tabular-nums">
        {value}
      </div>
    </div>
  );
}
